import { Link } from 'react-router-dom';
import { ShoppingCart, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Product } from '@/data/products';
import { useCart } from '@/context/CartContext';
import { productToAnalytics, trackEcommerce } from '@/utils/analytics';

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const { dispatch } = useCart();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(price);
  };

  const getMinPrice = () => {
    const prices = product.presentaciones.map(p => p.precio);
    return Math.min(...prices);
  };

  const selectedItem = () => {
    const presentation = product.presentaciones[0];
    return productToAnalytics(product, presentation.presentacion, presentation.precio);
  };

  const trackSelect = () => {
    trackEcommerce('select_item', [selectedItem()], { item_list_name: product.categoria });
  };

  const addToCart = () => {
    // Add the smallest presentation by default
    const defaultPresentation = product.presentaciones[0];
    trackEcommerce('add_to_cart', [selectedItem()]);

    dispatch({
      type: 'ADD_ITEM',
      payload: {
        productId: product.id,
        name: product.name,
        slug: product.slug,
        presentation: defaultPresentation.presentacion,
        quantity: 1,
        unitPrice: defaultPresentation.precio,
        image: product.imagen
      }
    });

    // Open cart to show the added item
    dispatch({ type: 'OPEN_CART' });
  };

  return (
    <div className="group bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
      {/* Product Image */}
      <div className="aspect-square bg-muted relative overflow-hidden">
        {product.imagen ? (
          <img
            src={product.imagen}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-primary/10 flex items-center justify-center">
            <span className="text-2xl text-primary font-bold">
              {product.name.charAt(0)}
            </span>
          </div>
        )}

        {/* Overlay with actions - appears on hover */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center space-x-2">
          <Button
            size="sm"
            variant="ghost"
            asChild
            className="bg-card hover:bg-primary hover:text-primary-foreground"
          >
            <Link to={`/producto/${product.slug}`} onClick={trackSelect}>
              <Eye className="w-4 h-4 mr-2" />
              Ver detalle
            </Link>
          </Button>

          <Button
            size="sm"
            onClick={addToCart}
            className="bg-primary hover:bg-primary-light"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Añadir
          </Button>
        </div>

        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <Badge variant="secondary" className="text-xs">
            {product.categoria}
          </Badge>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4">
        <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          <Link to={`/producto/${product.slug}`} onClick={trackSelect}>
            {product.name}
          </Link>
        </h3>

        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {product.descripcion}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {product.etiquetas.slice(0, 2).map((etiqueta) => (
            <Badge key={etiqueta} variant="outline" className="text-xs">
              {etiqueta}
            </Badge>
          ))}
          {product.etiquetas.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{product.etiquetas.length - 2}
            </Badge>
          )}
        </div>

        {/* Price */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-primary">
              desde {formatPrice(getMinPrice())}
            </p>
            <p className="text-xs text-muted-foreground">
              {product.presentaciones.length} presentaciones
            </p>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={addToCart}
            className="shrink-0 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            aria-label={`Añadir ${product.name} al carrito`}
          >
            <ShoppingCart className="w-4 h-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}